import type { captureRequestScope } from '@/lib/api/request-scope'
import { queryClient } from '@/lib/queryClient'
import { isResolvedUrl } from '@/lib/utils'
import { entityApi } from '@/modules/entities'
import { projectsApi } from '@/modules/projects'
import { useQuery } from '@tanstack/react-query'

import { ModuleTypeEnum } from '@scrambl/shared'

import { useRequestScope } from './useRequestScope'

interface UseModuleLogoProps {
  moduleId?: string
  moduleType: ModuleTypeEnum
  logoUrl?: string | null
}

type Scope = ReturnType<typeof captureRequestScope>
interface PendingLogo {
  moduleId: string
  resolve: (url: string | null) => void
  reject: (error: unknown) => void
}
interface LogoBatch {
  scope: Scope
  moduleType: ModuleTypeEnum
  requests: PendingLogo[]
}

// Only pending work lives outside Query. Never batch across logins/workspaces.
const batches = new Map<string, LogoBatch>()
const CACHE_TTL = 10 * 60 * 1000

async function flushBatch(key: string, batch: LogoBatch) {
  batches.delete(key)
  try {
    batch.scope.assertCurrent()
    const ids = [...new Set(batch.requests.map(request => request.moduleId))]
    const urls =
      batch.moduleType === ModuleTypeEnum.ENTITY
        ? await entityApi.bulkGetLogoUrls(ids)
        : await projectsApi.bulkGetLogoUrls(ids)
    batch.scope.assertCurrent()
    for (const request of batch.requests)
      request.resolve(urls[request.moduleId] ?? null)
  } catch (error) {
    for (const request of batch.requests) request.reject(error)
  }
}

function enqueueLogoRequest(
  scope: Scope,
  moduleType: ModuleTypeEnum,
  moduleId: string,
): Promise<string | null> {
  scope.assertCurrent()
  const key = JSON.stringify([scope.identity, scope.generation, moduleType])
  let batch = batches.get(key)
  if (!batch) {
    batch = { scope, moduleType, requests: [] }
    batches.set(key, batch)
    const queued = batch
    setTimeout(() => void flushBatch(key, queued), 50)
  }
  const queued = batch
  return new Promise((resolve, reject) => {
    queued.requests.push({ moduleId, resolve, reject })
  })
}

/** Batch signing requests while binding cached results to their source and session. */
export function useModuleLogo({
  moduleId,
  moduleType,
  logoUrl,
}: UseModuleLogoProps) {
  const scope = useRequestScope()
  const supported =
    moduleType === ModuleTypeEnum.ENTITY ||
    moduleType === ModuleTypeEnum.PROJECT
  const needsSigning =
    !!moduleId && !!logoUrl && supported && !isResolvedUrl(logoUrl)
  const query = useQuery(
    {
      queryKey: [
        'module-logo',
        moduleType,
        moduleId,
        logoUrl,
        scope.identity,
        scope.generation,
      ],
      queryFn: () => enqueueLogoRequest(scope, moduleType, moduleId!),
      enabled: needsSigning && !!scope.identity,
      staleTime: CACHE_TTL,
      gcTime: CACHE_TTL,
    },
    queryClient,
  )

  return {
    signedLogoUrl: needsSigning
      ? query.isError
        ? null
        : (query.data ?? null)
      : moduleId
        ? logoUrl || null
        : null,
    isLoading: needsSigning && query.isFetching,
  }
}

/** Invalidate every source/session variant after a logo upload or removal. */
export function invalidateModuleLogo(
  moduleType: ModuleTypeEnum,
  moduleId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['module-logo', moduleType, moduleId],
  })
}
